import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, from, of } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';

import {
  Firestore,
  collection,
  collectionData,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
  startAfter,
  getDocs,
  QueryDocumentSnapshot,
  getCountFromServer,
  or,
  and
} from '@angular/fire/firestore';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';

import { environment } from '../../environments/environment';

export interface AppNotification {
  id?: string;
  recipientId: string;
  title: string;
  body: string;
  data?: any;
  read: boolean;
  createdAt: Timestamp | Date;
  type?: string;
  emitter?: {
    emitterId: string;
    emitterName: string;
  };
  message?: string;
  resourceType?: string;
}

export interface NotificationReply {
  id?: string;
  message: string;
  senderId: string;
  senderName?: string;
  createdAt: Timestamp | Date;
}

const base_url = environment.base_url;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly firestore = inject(Firestore);
  private readonly messaging = inject(Messaging);
  private readonly http = inject(HttpClient);

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  // Storage keys for FCM token caching
  private readonly FCM_TOKEN_KEY = 'fcm_token';
  private readonly FCM_TOKEN_TIMESTAMP_KEY = 'fcm_token_timestamp';
  private readonly TOKEN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in ms

  private get token(): string {
    return localStorage.getItem('token') || '';
  }

  private get headers() {
    return {
      headers: {
        'x-token': this.token
      }
    };
  }

  // Request FCM notification permission and save token to backend
  requestPermissionAndSave(): Observable<string | null> {
    return from(Notification.requestPermission()).pipe(
      switchMap(permission => {
        if (permission === 'granted') {
          return from(getToken(this.messaging, {
            vapidKey: environment.firebase.vapidKey
          }));
        }
        return of(null);
      }),
      switchMap(token => {
        if (token) {
          return this.saveTokenIfNeeded(token);
        }
        return of(null);
      }),
      catchError(() => of(null))
    );
  }

  // Check if token needs to be saved to backend
  private saveTokenIfNeeded(newToken: string): Observable<string> {
    const storedToken = localStorage.getItem(this.FCM_TOKEN_KEY);
    const storedTimestamp = localStorage.getItem(this.FCM_TOKEN_TIMESTAMP_KEY);
    const now = Date.now();

    // Determine if we need to update the backend
    const tokenChanged = storedToken !== newToken;
    const tokenExpired = !storedTimestamp || (now - parseInt(storedTimestamp, 10)) > this.TOKEN_REFRESH_INTERVAL;
    const needsUpdate = tokenChanged || tokenExpired;

    if (!needsUpdate) {
      // Token is cached and still valid, no need to call backend
      return of(newToken);
    }

    // Save token to backend
    return this.http.put<any>(`${base_url}/users/notification/fcm-token`, { fcmToken: newToken }, this.headers).pipe(
      tap(() => {
        // Update localStorage with new token and timestamp
        localStorage.setItem(this.FCM_TOKEN_KEY, newToken);
        localStorage.setItem(this.FCM_TOKEN_TIMESTAMP_KEY, now.toString());
      }),
      map(() => newToken),
      catchError(() => {
        // Even if backend fails, cache the token to avoid repeated failed requests
        // But use a shorter expiry so it retries sooner
        localStorage.setItem(this.FCM_TOKEN_KEY, newToken);
        localStorage.setItem(this.FCM_TOKEN_TIMESTAMP_KEY, (now - this.TOKEN_REFRESH_INTERVAL + 3600000).toString()); // Retry in 1 hour
        return of(newToken);
      })
    );
  }

  // Clear FCM token cache (call this on logout)
  clearFcmTokenCache(): void {
    localStorage.removeItem(this.FCM_TOKEN_KEY);
    localStorage.removeItem(this.FCM_TOKEN_TIMESTAMP_KEY);
  }

  // Get inbox notifications stream for a specific user
  getInbox(userId: string): Observable<AppNotification[]> {
    const notificationsRef = collection(this.firestore, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      or(
        where('recipientId', '==', userId),
        where('recipientIds', 'array-contains', userId)
      ),
      orderBy('createdAt', 'desc')
    );

    return collectionData(notificationsQuery, { idField: 'id' }).pipe(
      map(notifications => notifications as AppNotification[])
    );
  }

  // Get total unread count
  getUnreadCount(userId: string): Observable<number> {
    const notificationsRef = collection(this.firestore, 'notifications');
    
    const unreadQuery = query(
      notificationsRef,
      and(
        or(
          where('recipientId', '==', userId),
          where('recipientIds', 'array-contains', userId)
        ),
        where('read', '==', false)
      )
    );

    // Real-time stream for unread count
    return collectionData(unreadQuery, { idField: 'id' }).pipe(
      map(notifications => notifications.length),
      tap(count => this.unreadCountSubject.next(count))
    );
  }

  // Get total unread count (one-time fetch, uses aggregation)
  getUnreadCountOnce(userId: string): Observable<number> {
    const notificationsRef = collection(this.firestore, 'notifications');
    
    const unreadQuery = query(
      notificationsRef,
      and(
        or(
          where('recipientId', '==', userId),
          where('recipientIds', 'array-contains', userId)
        ),
        where('read', '==', false)
      )
    );

    return from(getCountFromServer(unreadQuery)).pipe(
      map(snapshot => snapshot.data().count),
      tap(count => this.unreadCountSubject.next(count))
    );
  }

  // Get all unread notifications
  getUnreadNotifications(userId: string): Observable<AppNotification[]> {
    const notificationsRef = collection(this.firestore, 'notifications');
    
    const unreadQuery = query(
      notificationsRef,
      and(
        or(
          where('recipientId', '==', userId),
          where('recipientIds', 'array-contains', userId)
        ),
        where('read', '==', false)
      )
    );

    return from(getDocs(unreadQuery)).pipe(
      map(snapshot => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as AppNotification));
        
        // Sort by createdAt descending (newest first)
        return notifications.sort((a, b) => {
          const dateA = (a.createdAt as any)?.toDate ? (a.createdAt as any).toDate() : new Date(a.createdAt as any);
          const dateB = (b.createdAt as any)?.toDate ? (b.createdAt as any).toDate() : new Date(b.createdAt as any);
          return dateB.getTime() - dateA.getTime();
        });
      })
    );
  }

  // Get paginated notifications (for lazy loading)
  getInboxPaginated(
    userId: string, 
    pageSize: number = 20, 
    lastDoc?: QueryDocumentSnapshot<any>
  ): Observable<{ notifications: AppNotification[], lastDoc: QueryDocumentSnapshot<any> | null, hasMore: boolean }> {
    const notificationsRef = collection(this.firestore, 'notifications');
    
    let notificationsQuery = lastDoc
      ? query(
          notificationsRef,
          or(
            where('recipientId', '==', userId),
            where('recipientIds', 'array-contains', userId)
          ),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        )
      : query(
          notificationsRef,
          or(
            where('recipientId', '==', userId),
            where('recipientIds', 'array-contains', userId)
          ),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        );

    return from(getDocs(notificationsQuery)).pipe(
      map(snapshot => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as AppNotification));
        
        const newLastDoc = snapshot.docs.length > 0 
          ? snapshot.docs[snapshot.docs.length - 1] 
          : null;
        
        return {
          notifications,
          lastDoc: newLastDoc,
          hasMore: snapshot.docs.length === pageSize
        };
      })
    );
  }

  // Mark a notification as read in Firestore
  markAsRead(notificationId: string): Observable<void> {
    const notificationRef = doc(this.firestore, 'notifications', notificationId);
    return from(updateDoc(notificationRef, { read: true }));
  }

  markAsUnread(notificationId: string): Observable<void> {
    const notificationRef = doc(this.firestore, 'notifications', notificationId);
    return from(updateDoc(notificationRef, { read: false }));
  }

  // Mark all notifications as read for a user
  markAllAsRead(notifications: AppNotification[]): Observable<void[]> {
    const unreadNotifications = notifications.filter(n => !n.read && n.id);
    const updatePromises = unreadNotifications.map(n => 
      updateDoc(doc(this.firestore, 'notifications', n.id!), { read: true })
    );
    return from(Promise.all(updatePromises));
  }

  // Listen for foreground FCM messages
  onForegroundMessage(): Observable<any> {
    return new Observable(subscriber => {
      // Release the Firebase listener when the component unsubscribes (e.g. logout).
      return onMessage(this.messaging, (payload) => {
        subscriber.next(payload);
      });
    });
  }

  // Send a reply to a notification
  sendReply(notificationId: string | undefined, message: string): Observable<{ ok: boolean; msg: string; replyId: string }> {
    if (!notificationId || !message.trim()) {
      return of({ ok: false, msg: 'Invalid notification ID or message', replyId: '' });
    }

    return this.http.post<{ ok: boolean; msg: string; replyId: string }>(
      `${base_url}/notifications/${notificationId}/reply`,
      { message: message.trim() },
      this.headers
    );
  }

  // Get replies for a notification
  getReplies(notificationId: string): Observable<NotificationReply[]> {
    const repliesRef = collection(this.firestore, 'notifications', notificationId, 'replies');
    const repliesQuery = query(repliesRef, orderBy('createdAt', 'asc'));

    return collectionData(repliesQuery, { idField: 'id' }).pipe(
      map(replies => replies as NotificationReply[])
    );
  }
}
